import { useState, useMemo } from "react";
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Stack,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch } from "@tabler/icons-react";
import AdminSectionCard from "../../components/admin/shared/AdminSectionCard";
import {
  adminSections,
  canViewAdminMenuItem,
} from "../../data/adminNavigation";
import { useAuthStore } from "../../store";

export default function AdminPage() {
  const profile = useAuthStore((s) => s.profile);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 200);

  // Filter sections based on permissions and search
  const filteredSections = useMemo(() => {
    const permissionFiltered = adminSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          canViewAdminMenuItem(item, profile)
        ),
      }))
      .filter((section) => section.items.length > 0);

    if (!debouncedSearch) return permissionFiltered;
    const query = debouncedSearch.toLowerCase();
    return permissionFiltered
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.label.toLowerCase().includes(query) ||
            section.label.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [debouncedSearch, profile]);

  return (
    <Container size="xl" py="md">
      <Stack gap="xl">
        <div>
          <Title order={1}>Administration</Title>
          <Text size="lg" c="dimmed" mt="xs">
            Manage system settings, reference data, and configurations
          </Text>
        </div>

        <TextInput
          placeholder="Search admin pages..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="md"
          style={{ maxWidth: 400 }}
        />

        {filteredSections.length === 0 && debouncedSearch && (
          <Text c="dimmed" ta="center" py="xl">
            No admin pages found matching "{debouncedSearch}"
          </Text>
        )}

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {filteredSections.map((section) => (
            <AdminSectionCard
              key={section.id}
              title={section.label}
              description={section.description}
              items={section.items}
              icon={<section.icon size={28} />}
              color={section.color}
            />
          ))}
        </SimpleGrid>
      </Stack>
    </Container>
  );
}
